import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

// Configure axios to send credentials with every request
axios.defaults.withCredentials = true;

// Add axios request interceptor
const requestInterceptor = axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('session_token');
    if (token) {
      if (!config.headers) {
        config.headers = {};
      }
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // Envoyer le timezone IANA du client pour que le backend aligne "aujourd'hui" sur l'heure locale
    // (Ex: 'Africa/Abidjan' pour la Côte d'Ivoire, 'Europe/Paris' pour une future filiale...)
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) {
        if (!config.headers) config.headers = {};
        config.headers['X-Client-Timezone'] = tz;
      }
    } catch (_) { /* navigateur sans Intl, on laisse UTC par défaut côté backend */ }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add axios response interceptor for 401 errors
const responseInterceptor = axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem('session_token');
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Try with cookie first, fallback to localStorage token
      const token = localStorage.getItem('session_token');
      const config = { withCredentials: true };
      
      if (token && !document.cookie.includes('session_token')) {
        config.headers = { 'Authorization': `Bearer ${token}` };
      }
      
      const response = await axios.get(`${API}/auth/me`, config);
      setUser(response.data);
      setAuthenticated(true);
    } catch (error) {
      setUser(null);
      setAuthenticated(false);
      localStorage.removeItem('session_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(response.data.user);
      setAuthenticated(true);
      return response.data.user;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      setAuthenticated(false);
      localStorage.removeItem('session_token');
    } catch (error) {
      console.error('Erreur logout:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, authenticated, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans AuthProvider');
  }
  return context;
};
